import { queryClient } from "App";
import { get } from "lodash";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getEmailTemplateTableColumns,
  getEmailTemplateTableFilters,
} from "routes/email/components/EmailTemplateTableColumnsAndFilters";
import { EMAIL_TEMPLATE_TABLE_CONSTANTS } from "routes/email/EmailTemplateTableV3";
import EmailTemplateService from "services/EmailTemplate";
import TagService from "services/Tag";
import UserService from "services/User";
import CustomFieldService from "services/CustomField";
import { buildCustomFieldFilters } from "components/BaseTableV3/table-helpers";
import { addCustomFieldsColumns, addResizedColumns } from "./useTableConfig";
import tagConstants from "constants/Tag";

export const invalidateEmailTemplateTableQuery = async () => {
  // Awaited together: each call resolves only once its matching active queries
  // have refetched, so chaining them would serialise the round trips.
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [EMAIL_TEMPLATE_TABLE_CONSTANTS.queryKey],
    }),
    // Both filter lists are distinct values read off the template rows.
    queryClient.invalidateQueries({ queryKey: ["email-template-options"] }),
    queryClient.invalidateQueries({
      queryKey: ["email-template-custom-fields"],
    }),
  ]);
};

const fetchTags = async className => {
  const taggableModels = tagConstants.taggableModels;
  const model = Object.keys(taggableModels).find(
    key => taggableModels[key] === className
  );

  const tags = await TagService.getTags({ model: model, for_filtering: true });
  return tags;
};

const fetchOptions = async () => {
  const options = await EmailTemplateService.getTags();
  return options;
};

const fetchCustomFields = async () => {
  const customFields = await EmailTemplateService.getCustomFields();
  const configs =
    await CustomFieldService.getCustomFieldConfigsForModel("EmailTemplate");
  return [
    {
      accessorPrefix: "custom_fields",
      headerPrefix: "Email Templates",
      fields: customFields,
      model: "EmailTemplate",
      configs,
    },
  ];
};

export const useEmailTemplateTable = ({
  className = null,
  tableName = null,
}) => {
  const { data: tags, isSuccess: isTagsFetched } = useQuery({
    queryKey: ["tags", className],
    queryFn: () => fetchTags(className),
  });

  const { data: options, isSuccess: isOptionsFetched } = useQuery({
    queryKey: ["email-template-options"],
    queryFn: fetchOptions,
  });

  const { data: customFields, isSuccess: isCustomFieldsFetched } = useQuery({
    queryKey: ["email-template-custom-fields"],
    queryFn: fetchCustomFields,
  });

  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const filters = useMemo(() => {
    if (!isTagsFetched || !isCustomFieldsFetched) {
      return [];
    }
    const finalFilters = getEmailTemplateTableFilters({
      tags,
    });
    finalFilters.push(
      ...buildCustomFieldFilters(
        customFields,
        finalFilters.map(f => f.title)
      )
    );

    return finalFilters;
  }, [tags, customFields, isTagsFetched, isCustomFieldsFetched]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched || !isOptionsFetched || !isCustomFieldsFetched)
      return [];
    const initialColumns = getEmailTemplateTableColumns({
      options,
    });

    const emailTemplateTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );
    const withCustomFieldsColumns = addCustomFieldsColumns({
      columns: initialColumns,
      customFields,
      visibleColumns: emailTemplateTableSettings?.visible_columns || [],
    });

    const withResizedColumns = addResizedColumns({
      columns: withCustomFieldsColumns,
      resizedColumns: emailTemplateTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [
    tableName,
    tableSettings,
    isTableSettingsFetched,
    options,
    isOptionsFetched,
    isCustomFieldsFetched,
    customFields,
  ]);

  return { filters, columns, tags, options, customFields };
};
